import React, { useState, useEffect } from 'react';
import { getTaskExecutions, TaskExecution } from '../../services/scheduledTasksApi';
import { formatRelativeTime } from '../../utils/dateFormatters';
import { EmptyState, Modal, Spinner, StatusPill } from '../ui';
import type { StatusVariant } from '../ui';

interface TaskExecutionHistoryProps {
  taskId: string;
  onClose: () => void;
}

const TaskExecutionHistory: React.FC<TaskExecutionHistoryProps> = ({ taskId, onClose }) => {
  const [executions, setExecutions] = useState<TaskExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExecutions = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedExecutions = await getTaskExecutions(taskId, 20);
        setExecutions(fetchedExecutions);
      } catch (err: any) {
        setError(err.message || 'Failed to load execution history');
      } finally {
        setLoading(false);
      }
    };
    fetchExecutions();
  }, [taskId]);

  const getStatusVariant = (status: string): StatusVariant => {
    switch (status) {
      case 'completed':
        return 'ok';
      case 'failed':
        return 'flagged';
      case 'running':
        return 'approved';
      default:
        return 'draft';
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    executionList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    executionCard: {
      backgroundColor: 'var(--bg-surface-1)',
      borderRadius: '8px',
      padding: '1rem',
      border: '1px solid var(--border-1)',
    },
    executionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    executionTime: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: 'var(--fg-1)',
    },
    executionDetails: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '0.5rem',
      marginTop: '0.75rem',
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.125rem',
    },
    detailLabel: {
      fontSize: 11,
      color: 'var(--fg-4)',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
    },
    detailValue: {
      fontSize: '0.8rem',
      color: 'var(--fg-1)',
    },
    resultPreview: {
      fontSize: '0.8rem',
      color: 'var(--fg-2)',
      backgroundColor: 'var(--bg-surface-3)',
      padding: '0.5rem',
      borderRadius: 'var(--radius-sm)',
      marginTop: '0.5rem',
      maxHeight: '3rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    errorMessage: {
      fontSize: '0.8rem',
      color: 'var(--danger)',
      backgroundColor: 'var(--danger-soft)',
      border: '1px solid var(--cg-terra-200)',
      padding: '0.5rem',
      borderRadius: 'var(--radius-md)',
      marginTop: '0.5rem',
    },
    viewLink: {
      display: 'inline-block',
      marginTop: '0.75rem',
      fontSize: '0.8rem',
      color: 'var(--accent)',
      textDecoration: 'none',
      fontWeight: 500,
    },
  };

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (startedAt: string, completedAt?: string): string => {
    if (!completedAt) return 'In progress...';
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;

    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
    return `${Math.round(durationMs / 60000)}m`;
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Execution history"
      eyebrow="Scheduled task"
      width={700}
    >
      <div>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Spinner label="Loading execution history…" />
            </div>
          ) : error ? (
            <div style={{ color: 'var(--danger)', textAlign: 'center', padding: '2rem' }}>
              {error}
            </div>
          ) : executions.length === 0 ? (
            <EmptyState
              brand={false}
              maxWidth={420}
              title="No executions yet"
              subtitle="This task hasn't run yet. Executions will appear here after the scheduled time."
            />
          ) : (
            <div style={styles.executionList}>
              {executions.map((execution) => (
                <div key={execution.id} style={styles.executionCard}>
                  <div style={styles.executionHeader}>
                    <span style={styles.executionTime}>
                      {formatDateTime(execution.startedAt)}
                    </span>
                    <StatusPill variant={getStatusVariant(execution.status)} size="sm">
                      {execution.status}
                    </StatusPill>
                  </div>

                  <div style={styles.executionDetails}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Skill</span>
                      <span style={styles.detailValue}>{execution.skillName}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Duration</span>
                      <span style={styles.detailValue}>
                        {formatDuration(execution.startedAt, execution.completedAt)}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Email</span>
                      <span style={styles.detailValue}>
                        {execution.emailSentAt
                          ? `Sent ${formatRelativeTime(execution.emailSentAt)}`
                          : execution.emailError
                            ? <span style={{ color: 'var(--danger)' }}>Failed</span>
                            : 'Pending'
                        }
                      </span>
                    </div>
                  </div>

                  {execution.result && (
                    <div style={styles.resultPreview} title={execution.result}>
                      {execution.result.substring(0, 200)}
                      {execution.result.length > 200 ? '...' : ''}
                    </div>
                  )}

                  {execution.error && (
                    <div style={styles.errorMessage}>
                      Error: {execution.error}
                    </div>
                  )}

                  {execution.emailError && (
                    <div style={{ ...styles.errorMessage, backgroundColor: 'var(--warning-soft)', color: 'var(--warning)' }}>
                      Email not sent: {execution.emailError}
                    </div>
                  )}

                  {execution.shareLinkUrl && (
                    <a
                      href={execution.shareLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.viewLink}
                    >
                      View Full Result in App →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </Modal>
  );
};

export default TaskExecutionHistory;
